/**
 * Système de rate limiting côté client pour protéger contre les abus
 * et les attaques DDoS
 */

interface RateLimitConfig {
  maxRequests: number;
  windowMs: number;
  blockDurationMs: number;
}

interface RateLimitEntry {
  count: number;
  firstRequest: number;
  blockedUntil?: number;
}

class ClientRateLimiter {
  private requests: Map<string, RateLimitEntry> = new Map();
  private config: RateLimitConfig;

  constructor(config: RateLimitConfig = {
    maxRequests: 10, // 10 requêtes
    windowMs: 60000, // par minute
    blockDurationMs: 60000 // blocage de 1 minute
  }) {
    this.config = config;
    
    // Nettoyer les entrées expirées toutes les 5 minutes
    setInterval(() => {
      this.cleanup();
    }, 300000);
  }

  /**
   * Vérifie si une requête est autorisée pour un identifiant donné
   */
  isAllowed(identifier: string): { allowed: boolean; remaining: number; resetTime: number } {
    const now = Date.now();
    const entry = this.requests.get(identifier);

    // Si l'utilisateur est bloqué
    if (entry?.blockedUntil && now < entry.blockedUntil) {
      return {
        allowed: false,
        remaining: 0,
        resetTime: entry.blockedUntil
      };
    }

    // Si pas d'entrée ou fenêtre expirée, créer une nouvelle entrée
    if (!entry || (now - entry.firstRequest) > this.config.windowMs) {
      this.requests.set(identifier, {
        count: 1,
        firstRequest: now
      });
      
      return {
        allowed: true,
        remaining: this.config.maxRequests - 1,
        resetTime: now + this.config.windowMs
      };
    }

    // Vérifier si la limite est atteinte
    if (entry.count >= this.config.maxRequests) {
      // Bloquer l'utilisateur
      entry.blockedUntil = now + this.config.blockDurationMs;
      
      return {
        allowed: false,
        remaining: 0,
        resetTime: entry.blockedUntil
      };
    }

    // Incrémenter le compteur
    entry.count++;
    
    return {
      allowed: true,
      remaining: this.config.maxRequests - entry.count,
      resetTime: entry.firstRequest + this.config.windowMs
    };
  }

  /**
   * Nettoie les entrées expirées
   */
  private cleanup(): void {
    const now = Date.now();
    
    for (const [identifier, entry] of this.requests.entries()) {
      const isExpired = (now - entry.firstRequest) > this.config.windowMs;
      const isBlockExpired = entry.blockedUntil && now > entry.blockedUntil;
      
      if (isExpired && isBlockExpired) {
        this.requests.delete(identifier);
      }
    }
  }

  /**
   * Obtient les statistiques pour un identifiant
   */
  getStats(identifier: string): { count: number; remaining: number; resetTime: number; isBlocked: boolean } {
    const entry = this.requests.get(identifier);
    const now = Date.now();
    
    if (!entry) {
      return {
        count: 0,
        remaining: this.config.maxRequests,
        resetTime: now + this.config.windowMs,
        isBlocked: false
      };
    }

    const isBlocked = entry.blockedUntil ? now < entry.blockedUntil : false;
    const resetTime = isBlocked ? entry.blockedUntil! : entry.firstRequest + this.config.windowMs;
    
    return {
      count: entry.count,
      remaining: Math.max(0, this.config.maxRequests - entry.count),
      resetTime,
      isBlocked
    };
  }

  /**
   * Réinitialise le compteur pour un identifiant
   */
  reset(identifier: string): void {
    this.requests.delete(identifier);
  }

  /**
   * Obtient tous les identifiants actuellement bloqués
   */
  getBlockedIdentifiers(): string[] {
    const now = Date.now();
    const blocked: string[] = [];
    
    for (const [identifier, entry] of this.requests.entries()) {
      if (entry.blockedUntil && now < entry.blockedUntil) {
        blocked.push(identifier);
      }
    }
    
    return blocked;
  }
}

// Instance singleton pour l'application
export const rateLimiter = new ClientRateLimiter();

/**
 * Hook React pour utiliser le rate limiting
 */
export const useRateLimit = (identifier: string = 'default') => {
  const checkRateLimit = () => {
    return rateLimiter.isAllowed(identifier);
  };

  const getStats = () => {
    return rateLimiter.getStats(identifier);
  };

  const reset = () => {
    rateLimiter.reset(identifier);
  };

  return {
    checkRateLimit,
    getStats,
    reset
  };
};

/**
 * Fonction utilitaire pour générer un identifiant unique basé sur l'IP
 * (en production, ceci devrait être fait côté serveur)
 */
export const generateClientIdentifier = (): string => {
  // En production, utiliser l'IP réelle ou un token de session
  // Pour le développement, utiliser une combinaison de facteurs

  // Vérifier si on est côté client
  if (typeof window === 'undefined') {
    // Côté serveur, générer un ID simple
    return `server-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  const userAgent = navigator.userAgent;
  const language = navigator.language;
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  
  // Créer un hash simple (en production, utiliser une vraie fonction de hash)
  const combined = `${userAgent}-${language}-${timezone}`;
  let hash = 0;
  
  for (let i = 0; i < combined.length; i++) {
    const char = combined.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convertir en 32-bit integer
  }
  
  return `client_${Math.abs(hash)}`;
};

/**
 * Interface pour les erreurs de rate limit
 */
interface RateLimitError extends Error {
  code: string;
  resetTime: number;
}

/**
 * Middleware de rate limiting pour les appels API
 */
export const withRateLimit = async <T>(
  apiCall: () => Promise<T>,
  identifier: string = 'default'
): Promise<T> => {
  const rateLimitResult = rateLimiter.isAllowed(identifier);

  if (!rateLimitResult.allowed) {
    const error = new Error('Rate limit exceeded') as RateLimitError;
    error.code = 'RATE_LIMIT_EXCEEDED';
    error.resetTime = rateLimitResult.resetTime;
    throw error;
  }

  return apiCall();
};
